import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import {ThemeProvider} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import logo from './logo.png';
import {
  AppBar,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  IconButton as MuiIconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@material-ui/core';
import PaletteIcon from '@material-ui/icons/Palette';
import {Route, Switch, useHistory, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import MenuIcon from '@material-ui/icons/Menu';
import HomeIcon from '@material-ui/icons/Home';
import AppsIcon from '@material-ui/icons/Apps';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import LanguageIcon from '@material-ui/icons/Language';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import LockIcon from '@material-ui/icons/Lock';
import InfoIcon from '@material-ui/icons/Info';
import SettingsIcon from '@material-ui/icons/Settings';
import BackupIcon from '@material-ui/icons/Backup';
import {connect, disconnect, autoConnect, verifyAdminAuth, transceive, setAdminAuthenticated} from './actions';
import Overview from './Overview';
import Admin from "./Admin";
import Apdu from "./Apdu";
import Oath from "./Oath";
import Piv from "./Piv";
import OpenPGP from './OpenPGP';
import FIDO2 from './FIDO2';
import Pass from './Pass';
import Ndef from './Ndef';
import BrowserCompatibility from './BrowserCompatibility';
import CompatibilityReport from './CompatibilityReport';
import PinRecovery from './PinRecovery';
import SetupWizard from './SetupWizard';
import Backup from './Backup';
import {useSnackbar} from "notistack";
import {createCustomTheme, availableThemeNames, getThemeDisplayName, getSavedTheme, saveTheme} from './themes';
import {useTranslation} from 'react-i18next';
import './i18n'; // 导入i18n配置

const drawerWidth = 200;

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  title: {
    flexGrow: 1,
    fontSize: '1.25rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.95rem',
      lineHeight: 1.2,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.85rem',
    },
  },
  toolbar: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    [theme.breakpoints.down('sm')]: {
      gap: '4px',
    },
  },
  connectButton: {
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      padding: theme.spacing(0.75, 1.5),
      fontSize: '0.8rem',
    },
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: '80vw',
    },
  },
  drawerPaper: {
    width: drawerWidth,
    [theme.breakpoints.down('sm')]: {
      width: '80vw',
      maxWidth: 280,
    },
  },
  icon: {
    paddingLeft: "20px",
    paddingTop: "10px",
    paddingBottom: "10px",
    width: "70%",
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

export default function App() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {t} = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [appletOpen, setAppletOpen] = useState(true);
  const [currentTheme, setCurrentTheme] = useState(getSavedTheme());
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [adminPinDialogOpen, setAdminPinDialogOpen] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const device = useSelector(state => state.device);
  const adminAuthenticated = useSelector(state => state.adminAuthenticated);
  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
    if (navigator.usb === undefined) {
      enqueueSnackbar(t('webusb.notSupported'), {
        variant: "warning",
        persist: true,
        action: (key) => (
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => history.push('/browser-check')}
            >
              {t('webusb.viewDetails')}
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => window.open('https://www.google.com/chrome/', '_blank')}
            >
              {t('webusb.downloadChrome')}
            </Button>
          </>
        )
      });
    } else {
      enqueueSnackbar(t('webusb.supported'), {variant: "success"});

      // Try to auto-connect to previously authorized device
      (async () => {
        try {
          const success = await dispatch(autoConnect());
          if (success) {
            console.log('✓ Auto-connected to previously authorized device');
          }
        } catch (e) {
          // 自动连接失败是正常的（设备未插入/未授权/繁忙）
          console.debug('Auto-connect skipped:', e.message || e);
        }
      })();
    }
  }, [enqueueSnackbar, dispatch, t, history]);

  // Periodically check admin authentication status from device
  useEffect(() => {
    if (!device) return;
    
    // Initial check (with logging)
    (async () => {
      const isAuthenticated = await verifyAdminAuth(dispatch, device, false);
      // 如果初始检查发现未认证，且不在 Overview/APDU 页面，弹出认证对话框
      const currentPath = window.location.pathname;
      if (!isAuthenticated && !currentPath.includes('/overview') && !currentPath.includes('/apdu')) {
        setAdminPinDialogOpen(true);
      }
    })();
    
    // Poll every 5 minutes to keep status in sync with device (silent mode)
    // 固件有 2 小时认证缓存，5 分钟检查一次足够
    const interval = setInterval(async () => {
      if (device) {
        await verifyAdminAuth(dispatch, device, true); // silent=true for periodic checks
      }
    }, 300000); // 5 minutes (300 seconds)
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]); // 只依赖 device，避免 dispatch 变化导致的重复执行

  // Check admin authentication on route change
  useEffect(() => {
    if (!device) {
      console.log(`📌 Route check skipped: no device`);
      return;
    }
    
    const currentPath = location.pathname;
    console.log(`📌 Route check: path=${currentPath}, adminAuthenticated=${adminAuthenticated}`);
    
    // Overview、APDU 和 SetupWizard 页面不需要 Admin 认证
    if (currentPath.includes('/overview') || currentPath.includes('/apdu') || currentPath.includes('/setup-wizard') || currentPath === '/') {
      console.log(`📌 Route check: ${currentPath} does not require Admin auth`);
      return;
    }
    
    // 检查当前 Redux 状态
    // 如果未认证，自动弹出认证对话框
    if (!adminAuthenticated) {
      console.log(`📌 Admin not authenticated on ${currentPath}, opening auth dialog`);
      setAdminPinDialogOpen(true);
    } else {
      console.log(`✅ Admin already authenticated on ${currentPath}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, device, adminAuthenticated]); // 监听路由和认证状态变化

  const connectUSB = useCallback(async () => {
    try {
      await dispatch(connect());
    } catch (e) {
      if (e.toString().indexOf("claim") >= 0) {
        enqueueSnackbar(`Failed to connect the device. Make sure you've closed other tabs or browsers.`, {variant: "error"});
      } else if (e.toString().indexOf("transfer") >= 0) {
        enqueueSnackbar(`Failed to connect the device. Make sure you've killed the GPG daemon and other CCID applications.`, {variant: "error"});
      } else {
        enqueueSnackbar(`Failed to connect the device: ${e}`, {variant: "error"});
      }
    }
  }, [dispatch, enqueueSnackbar]);

  const showOverview = useCallback(e => {
    setDrawerOpen(false);
    history.push('/');
  }, [history]);

  const showAdmin = useCallback(e => {
    setDrawerOpen(false);
    history.push('/admin');
  }, [history]);

  const showAPDU = useCallback(e => {
    setDrawerOpen(false);
    history.push('/apdu');
  }, [history]);

  const showOATH = useCallback(e => {
    setDrawerOpen(false);
    history.push('/oath');
  }, [history]);

  const showPIV = useCallback(e => {
    setDrawerOpen(false);
    history.push('/piv');
  }, [history]);

  const showOpenPGP = useCallback(e => {
    setDrawerOpen(false);
    history.push('/openpgp');
  }, [history]);

  const showFIDO2 = useCallback(e => {
    setDrawerOpen(false);
    history.push('/fido2');
  }, [history]);

  const showPASS = useCallback(e => {
    setDrawerOpen(false);
    history.push('/pass');
  }, [history]);

  const showNDEF = useCallback(e => {
    setDrawerOpen(false);
    history.push('/ndef');
  }, [history]);

  const showBrowserCheck = useCallback(e => {
    setDrawerOpen(false);
    history.push('/browser-check');
  }, [history]);

  const showSetupWizard = useCallback(e => {
    setDrawerOpen(false);
    history.push('/setup-wizard');
  }, [history]);

  const showPinRecovery = useCallback(e => {
    setDrawerOpen(false);
    history.push('/pin-recovery');
  }, [history]);

  const showCompatibilityReport = useCallback(e => {
    setDrawerOpen(false);
    history.push('/compatibility-report');
  }, [history]);

  const showBackup = useCallback(e => {
    setDrawerOpen(false);
    history.push('/backup');
  }, [history]);

  const handleThemeChange = useCallback((themeName) => {
    setCurrentTheme(themeName);
    saveTheme(themeName);
    setThemeMenuAnchor(null);
    enqueueSnackbar(`Switched to ${getThemeDisplayName(themeName)}`, {variant: "success"});
  }, [enqueueSnackbar]);

  const openThemeMenu = useCallback((event) => {
    setThemeMenuAnchor(event.currentTarget);
  }, []);

  const closeThemeMenu = useCallback(() => {
    setThemeMenuAnchor(null);
  }, []);

  const {i18n} = useTranslation();
  
  const openLangMenu = useCallback((event) => {
    setLangMenuAnchor(event.currentTarget);
  }, []);

  const closeLangMenu = useCallback(() => {
    setLangMenuAnchor(null);
  }, []);

  const handleLanguageChange = useCallback((lang) => {
    i18n.changeLanguage(lang);
    setLangMenuAnchor(null);
    enqueueSnackbar(`Language changed to ${lang === 'zh' ? '中文' : 'English'}`, {variant: "success"});
  }, [i18n, enqueueSnackbar]);

  const handleAdminChipClick = useCallback(async () => {
    if (adminAuthenticated) {
      // 已认证，刷新状态
      await verifyAdminAuth(dispatch, device);
    } else {
      // 未认证，打开PIN输入对话框
      setAdminPinDialogOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, device]); // 移除 adminAuthenticated 依赖，避免状态变化导致的重复执行

  const handleAdminPinVerify = useCallback(async () => {
    if (!adminPin) {
      enqueueSnackbar('Please enter Admin PIN', {variant: 'warning'});
      return;
    }

    // 🔒 安全增强：复制 PIN 后立即清除 state，防止内存泄露
    const pin = adminPin;
    setAdminPin('');

    try {
      // Select admin applet
      let res = await dispatch(transceive("00A4040005F000000000"));
      if (!res.endsWith("9000")) {
        throw new Error('Failed to select admin applet');
      }

      // Verify PIN
      const pinHex = Array.from(pin).map(c => 
        c.charCodeAt(0).toString(16).padStart(2, '0')
      ).join('');
      const lc = (pin.length).toString(16).padStart(2, '0');
      res = await dispatch(transceive(`00200000${lc}${pinHex}`));
      
      if (res.endsWith("9000")) {
        dispatch(setAdminAuthenticated(true));
        setAdminPinDialogOpen(false);
        enqueueSnackbar('Admin authenticated successfully', {variant: 'success'});
      } else if (res.match(/63C\d$/)) {
        const retries = parseInt(res.slice(-1), 16);
        enqueueSnackbar(`Wrong PIN, ${retries} retries left`, {variant: 'error'});
      } else if (res.endsWith("6983")) {
        enqueueSnackbar('Admin PIN is blocked', {variant: 'error'});
      } else {
        enqueueSnackbar(`Authentication failed: ${res}`, {variant: 'error'});
      }
    } catch (e) {
      enqueueSnackbar(`Error: ${e.message || e}`, {variant: 'error'});
    }
  }, [adminPin, dispatch, enqueueSnackbar, setAdminPin]);

  const handleAdminPinDialogClose = useCallback(() => {
    setAdminPinDialogOpen(false);
    setAdminPin('');
  }, []);

  return (
    <ThemeProvider theme={createCustomTheme(currentTheme)}>
      <div className={classes.root}>
        <AppBar position="static">
          <Toolbar className={classes.toolbar}>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setDrawerOpen(true)}>
              <MenuIcon/>
            </IconButton>
            <Typography variant="h6" className={classes.title}>
              GriffonOakey Web Console
            </Typography>
            <div className={classes.actionButtons}>
              {device && !isMobile && (
                <Tooltip title={
                  adminAuthenticated
                    ? "Admin authenticated (click to refresh)"
                    : "Admin not authenticated (click to login)"
                }>
                  <Chip
                    icon={adminAuthenticated ? <LockOpenIcon /> : <LockIcon />}
                    label={adminAuthenticated ? "Admin" : "No Admin"}
                    size="small"
                    onClick={handleAdminChipClick}
                    style={{
                      backgroundColor: adminAuthenticated ? '#4caf50' : '#757575',
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              )}
              {!isMobile && (
                <>
                  <MuiIconButton
                    className="language-toggle-button"
                    color="inherit"
                    onClick={openLangMenu}
                    aria-label="Switch Language"
                    title={`Language: ${i18n.language === 'zh' ? '中文' : 'English'}`}
                  >
                    <LanguageIcon />
                  </MuiIconButton>
                  <MuiIconButton
                    className="theme-toggle-button"
                    color="inherit"
                    onClick={openThemeMenu}
                    aria-label="Switch Theme"
                    title={`Current Theme: ${getThemeDisplayName(currentTheme)} (Click to switch)`}
                  >
                    <PaletteIcon />
                  </MuiIconButton>
                </>
              )}
              {
                device ?
                  <Button
                    variant="contained"
                    onClick={() => dispatch(disconnect())}
                    className={classes.connectButton}
                  >
                    {t('disconnect')}
                  </Button> :
                  <Button
                    variant="contained"
                    onClick={connectUSB}
                    className={classes.connectButton}
                  >
                    {t('connect')}
                  </Button>
              }
            </div>
          </Toolbar>
        </AppBar>
      <Drawer
        className={classes.drawer}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <img src={logo} alt="logo" className={classes.icon}/>
        <List>
          <Divider/>
          <ListItem button onClick={showOverview}>
            <ListItemIcon><HomeIcon/></ListItemIcon>
            <ListItemText>{t('menu.home')}</ListItemText>
          </ListItem>
          <ListItem button onClick={() => setAppletOpen(!appletOpen)}>
            <ListItemIcon><AppsIcon/></ListItemIcon>
            <ListItemText>{t('menu.applets')}</ListItemText>
            {appletOpen ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
          </ListItem>
          <Collapse in={appletOpen}>
            <List>
              <ListItem button onClick={showFIDO2} className={classes.nested}>
                <ListItemIcon><ArrowForwardIcon/></ListItemIcon>
                <ListItemText>{t('menu.fido2')}</ListItemText>
              </ListItem>
              <ListItem button onClick={showOATH} className={classes.nested}>
                <ListItemIcon><ArrowForwardIcon/></ListItemIcon>
                <ListItemText>{t('menu.oath')}</ListItemText>
              </ListItem>
              <ListItem button onClick={showOpenPGP} className={classes.nested}>
                <ListItemIcon><ArrowForwardIcon/></ListItemIcon>
                <ListItemText>{t('menu.openpgp')}</ListItemText>
              </ListItem>
              <ListItem button onClick={showPIV} className={classes.nested}>
                <ListItemIcon><ArrowForwardIcon/></ListItemIcon>
                <ListItemText>{t('menu.piv')}</ListItemText>
              </ListItem>
              <ListItem button onClick={showPASS} className={classes.nested}>
                <ListItemIcon><ArrowForwardIcon/></ListItemIcon>
                <ListItemText>{t('menu.pass')}</ListItemText>
              </ListItem>
              <ListItem button onClick={showNDEF} className={classes.nested}>
                <ListItemIcon><ArrowForwardIcon/></ListItemIcon>
                <ListItemText>{t('menu.ndef')}</ListItemText>
              </ListItem>
              <ListItem button onClick={showAdmin} className={classes.nested}>
                <ListItemIcon><ArrowForwardIcon/></ListItemIcon>
                <ListItemText>{t('menu.admin')}</ListItemText>
              </ListItem>
            </List>
          </Collapse>
          <ListItem button onClick={showAPDU} className={classes.nested}>
            <ListItemIcon><SyncAltIcon/></ListItemIcon>
            <ListItemText>{t('menu.apdu')}</ListItemText>
          </ListItem>
          <Divider/>
          <ListItem button onClick={showBrowserCheck}>
            <ListItemIcon><InfoIcon/></ListItemIcon>
            <ListItemText>{t('menu.browserCheck')}</ListItemText>
          </ListItem>
          <Divider/>
          <ListItem button onClick={showSetupWizard}>
            <ListItemIcon><SettingsIcon/></ListItemIcon>
            <ListItemText>{t('menu.setupWizard')}</ListItemText>
          </ListItem>
          <ListItem button onClick={showPinRecovery}>
            <ListItemIcon><LockOpenIcon/></ListItemIcon>
            <ListItemText>{t('menu.pinRecovery')}</ListItemText>
          </ListItem>
          <ListItem button onClick={showCompatibilityReport}>
            <ListItemIcon><SyncAltIcon/></ListItemIcon>
            <ListItemText>{t('menu.compatibilityReport')}</ListItemText>
          </ListItem>
          <ListItem button onClick={showBackup}>
            <ListItemIcon><BackupIcon/></ListItemIcon>
            <ListItemText>Backup & Restore</ListItemText>
          </ListItem>
        </List>
      </Drawer>

              <Switch>
          <Route path="/" exact component={Overview}/>
          <Route path="/browser-check" exact component={BrowserCompatibility}/>
          <Route path="/fido2" exact component={FIDO2}/>
          <Route path="/admin" exact component={Admin}/>
          <Route path="/apdu" exact component={Apdu}/>
          <Route path="/oath" exact component={Oath}/>
          <Route path="/piv" exact component={Piv}/>
          <Route path="/openpgp" exact component={OpenPGP}/>
          <Route path="/pass" exact component={Pass}/>
          <Route path="/ndef" exact component={Ndef}/>
          <Route path="/setup-wizard" exact component={SetupWizard}/>
          <Route path="/pin-recovery" exact component={PinRecovery}/>
          <Route path="/compatibility-report" exact component={CompatibilityReport}/>
          <Route path="/backup" exact component={Backup}/>
        </Switch>
      </div>
      
      {/* 主题切换菜单 */}
      <Menu
        anchorEl={themeMenuAnchor}
        keepMounted
        open={Boolean(themeMenuAnchor)}
        onClose={closeThemeMenu}
      >
        {availableThemeNames.map((themeName) => (
          <MenuItem
            key={themeName}
            onClick={() => handleThemeChange(themeName)}
            selected={currentTheme === themeName}
          >
            {getThemeDisplayName(themeName)}
          </MenuItem>
        ))}
      </Menu>
      
      {/* 语言切换菜单 */}
      <Menu
        anchorEl={langMenuAnchor}
        keepMounted
        open={Boolean(langMenuAnchor)}
        onClose={closeLangMenu}
      >
        <MenuItem 
          onClick={() => handleLanguageChange('en')}
          selected={i18n.language === 'en'}
        >
          English
        </MenuItem>
        <MenuItem 
          onClick={() => handleLanguageChange('zh')}
          selected={i18n.language === 'zh'}
        >
          中文简体
        </MenuItem>
      </Menu>

      {/* Admin PIN Dialog */}
      <Dialog 
        open={adminPinDialogOpen} 
        onClose={handleAdminPinDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={(e) => { e.preventDefault(); handleAdminPinVerify(); }}>
          <DialogTitle>Admin Authentication</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Enter Admin PIN to authenticate
            </Typography>
            {/* Hidden username field for accessibility */}
            <input type="text" name="username" autoComplete="username" style={{display: 'none'}} readOnly value="admin" />
            <TextField
              autoFocus
              margin="dense"
              label="Admin PIN"
              type="password"
              fullWidth
              value={adminPin}
              inputProps={{maxLength: 64}}
              onChange={(e) => setAdminPin(e.target.value)}
              placeholder="Default: 123456"
              autoComplete="current-password"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAdminPinDialogClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Verify
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
    </ThemeProvider>
    );
  }
